<template>
  <div ref="modalRoot" class="modal">
    <form @submit.prevent="submitApplication">
      <h3>Apply for Gigs</h3>

      <div class="modal-body">
      <!-- Name Field with Validation -->
      <input
        type="text"
        v-model="comedianName"
        placeholder="Your Name"
        required
        :class="{ 'invalid': hasBlurred.comedianName && !comedianNameValid, 'valid': comedianNameValid }"
        @blur="hasBlurred.comedianName = true"
      />
      <div v-if="hasBlurred.comedianName && !comedianNameValid" class="error-message">
        Please enter your name
      </div>

      <!-- Email Field with Validation -->
      <input
        type="email"
        v-model="email"
        placeholder="Your Email"
        required
        :class="{ 'invalid': hasBlurred.email && !emailValid, 'valid': emailValid }"
        @blur="hasBlurred.email = true"
      />
      <div v-if="hasBlurred.email && !emailValid" class="error-message">
        Please enter a valid email address
      </div>

      <!-- YouTube URL Field with Validation -->
      <input
        type="text"
        v-model="videoLink"
        placeholder="Video URL (YouTube unlisted preferred)"
        required
        :class="{ 'invalid': hasBlurred.videoLink && !videoLinkValid, 'valid': videoLinkValid }"
        @blur="hasBlurred.videoLink = true"
      />
      <div v-if="hasBlurred.videoLink && !videoLinkValid" class="error-message">
        Please provide a video URL
      </div>

      <!-- Facebook Field -->
      <input
        type="text"
        v-model="facebook"
        placeholder="Facebook Username"
      />

      <!-- Instagram Field -->
      <input
        type="text"
        v-model="instagram"
        placeholder="Instagram Username"
      />

      <!-- Any Other Info Field -->
      <textarea
        v-model="notes"
        placeholder="Anything else you want to tell us?"></textarea>
      </div>

      <!-- Submit and Cancel Buttons -->
      <div class="button-group">
        <button type="submit" :disabled="!formValid">Submit Application</button>
        <button type="button" @click="$emit('close')">Cancel</button>
      </div>
    </form>
  </div>
</template>

<script>
/** @see docs/APP_UI_SPEC.md — E1 (blocked emails skip confirmation send), E2 (return on insert error before email path) */
import { supabase } from '../supabaseClient';
import Swal from 'sweetalert2';
import { sanitizeSocialUsername } from '../utils/socialLinks.js';

export default {
  name: 'ApplicationForm',
  props: {
    eventIds: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      comedianName: '',
      email: '',
      videoLink: '',
      facebook: '',
      instagram: '',
      notes: '',
      hasBlurred: {
        comedianName: false,
        email: false,
        videoLink: false
      },
    };
  },
  mounted() {
    // Load saved email from localStorage
    const savedEmail = localStorage.getItem('comedianEmail');
    if (savedEmail) {
      this.email = savedEmail;
    }

    this.boundApplyModalViewport = () => this.applyModalViewport();
    this.$nextTick(() => this.applyModalViewport());
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', this.boundApplyModalViewport);
      window.visualViewport.addEventListener('scroll', this.boundApplyModalViewport);
    }
    window.addEventListener('resize', this.boundApplyModalViewport);
  },
  beforeUnmount() {
    if (window.visualViewport && this.boundApplyModalViewport) {
      window.visualViewport.removeEventListener('resize', this.boundApplyModalViewport);
      window.visualViewport.removeEventListener('scroll', this.boundApplyModalViewport);
    }
    window.removeEventListener('resize', this.boundApplyModalViewport);
  },
  computed: {
    comedianNameValid() {
      return this.comedianName.trim() !== '';
    },
    emailValid() {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(this.email);
    },
    videoLinkValid() {
      return this.videoLink.trim() !== '';
    },
    formValid() {
      return this.comedianNameValid && this.emailValid && this.videoLinkValid;
    }
  },
  methods: {
    applyModalViewport() {
      const el = this.$refs.modalRoot;
      if (!el) return;
      const narrow = window.matchMedia('(max-width: 768px)').matches;
      const vv = window.visualViewport;
      if (narrow && vv) {
        const pad = 12;
        el.style.top = `${vv.offsetTop + pad}px`;
        el.style.maxHeight = `${Math.max(160, vv.height - pad * 2)}px`;
        el.style.transform = 'translateX(-50%)';
      } else {
        el.style.top = '';
        el.style.maxHeight = '';
        el.style.transform = '';
      }
    },
    async submitApplication() {
      if (!this.formValid) {
        // Mark all fields as blurred to show all validation messages
        this.hasBlurred = {
          comedianName: true,
          email: true,
          videoLink: true
        };
        return;
      }

      try {
        // Save email to localStorage for future use
        localStorage.setItem('comedianEmail', this.email);

        const { error } = await supabase.from('applications').insert(
          this.eventIds.map((eventId) => ({
            event_id: eventId,
            comedian_name: this.comedianName,
            email: this.email,
            video_link: this.videoLink,
            facebook: sanitizeSocialUsername(this.facebook),
            instagram: sanitizeSocialUsername(this.instagram),
            notes: this.notes,
          }))
        );

        // Since we can't select the inserted data due to RLS,
        // we'll construct the application data manually for the email
        const applications = this.eventIds.map((eventId) => ({
          event_id: eventId,
          comedian_name: this.comedianName,
          email: this.email,
          video_link: this.videoLink,
          facebook: sanitizeSocialUsername(this.facebook),
          instagram: sanitizeSocialUsername(this.instagram),
          notes: this.notes,
        }));

        if (error) {
          console.error('Error submitting application:', error);
          Swal.fire({
            icon: 'error',
            title: 'Oops...',
            text: `An error occurred while submitting your application: ${error.message}`,
          });
          return;
        }

        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: 'Thanks so much for applying!',
        });
        this.$emit('close');
        // this.resetForm();

        // Check if this email is blocked before sending the confirmation email
        const { data: blockedRow } = await supabase
          .from('blocked_emails')
          .select('id')
          .eq('email', this.email.toLowerCase())
          .maybeSingle();

        if (blockedRow) {
          this.resetForm();
          return;
        }

        // After successful save, send the email
        const edgeFunctionUrl = `${supabase.supabaseUrl}/functions/v1/send-application-email`;

        // Fetch event details with venue information
        const { data: events, error: eventError } = await supabase
          .from('events')
          .select(`
            id,
            event_name,
            event_date,
            venue_id,
            venues (
              venue_name,
              venue_slug,
              address,
              google_maps_url,
              doors_time,
              show_time,
              show_end_time
            )
          `)
          .in('id', this.eventIds);

        if (eventError) {
          console.error('Error fetching event details:', eventError);
          Swal.fire({
            icon: 'error',
            title: 'Oops...',
            text: 'Failed to fetch event details. Please try again.',
          });
          return;
        }


        // Combine application data with event details for the payload
        const applicationData = applications.map((application) => {
          const event = events.find((e) => e.id === application.event_id);
          return {
            event_name: event.event_name,
            event_date: event.event_date,
            venue_name: event.venues?.venue_name || 'Comedy Night',
            venue_slug: event.venues?.venue_slug || '',
            venue_address: event.venues?.address || '',
            venue_google_maps_url: event.venues?.google_maps_url || '',
            venue_doors_time: event.venues?.doors_time || '7:30 PM',
            venue_show_time: event.venues?.show_time || '8:00 PM',
            venue_show_end_time: event.venues?.show_end_time || '10:00 PM',
            comedian_name: application.comedian_name,
            email: application.email,
            video_link: application.video_link,
            facebook: application.facebook,
            instagram: application.instagram,
            notes: application.notes,
          };
        });


        const payload = {
          email: this.email,
          applicationData: applicationData, // Ensure this is populated correctly
        };

        console.log('Payload being sent:', JSON.stringify(payload, null, 2));

        const emailResponse = await fetch(edgeFunctionUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${supabase.supabaseKey}`, // Use the initialized key from supabaseClient.js
          },
          body: JSON.stringify(payload),
        });

        if (!emailResponse.ok) {
          const emailError = await emailResponse.text();
          console.error('Email send error:', emailError);
          Swal.fire({
            icon: 'error',
            title: 'Email Error',
            text: 'Failed to send your application email',
          });
        } else {
          Swal.fire({
            icon: 'success',
            title: 'Success!',
            text: 'Your application has been emailed to the club!',
          });
          this.resetForm();
        }
      } catch (err) {
        console.error('Unexpected error:', err);
        Swal.fire({
          icon: 'error',
          title: 'Unexpected Error',
          text: `An unexpected error occurred: ${err.message}`,
        });
      }
    },
    resetForm() {
      this.comedianName = '';
      this.email = '';
      this.videoLink = '';
      this.facebook = '';
      this.instagram = '';
      this.notes = '';
      this.hasBlurred = {
        comedianName: false,
        email: false,
        videoLink: false
      };
    },
  },
};
</script>

<style scoped>
.modal {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: #ffffff;
  padding: 20px;
  border-radius: 10px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  width: 90%;
  max-width: 400px;
  max-height: min(90vh, calc(100dvh - 32px));
  z-index: 1000;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.modal form {
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex: 1;
  overflow: hidden;
}

.modal-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.modal-body input:first-of-type {
  margin-top: 0;
}

input.invalid {
  border-color: red;
}

input.valid {
  border-color: green;
}

.error-message {
  color: red;
  font-size: 12px;
  margin: -8px 0 8px 0;
  padding-left: 2px;
}

.modal h3 {
  flex-shrink: 0;
  margin-top: 0;
  text-align: center;
  color: #333;
}

.modal input,
.modal textarea {
  width: calc(100% - 20px);
  padding: 10px;
  margin: 10px 0;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-size: 16px;
  font-family: inherit;  /* This ensures it inherits the font from the parent */
  box-sizing: border-box;
}

/* Specific textarea styles to match input exactly */
.modal textarea {
  resize: vertical;  /* Allows vertical resizing only */
  min-height: 100px;  /* Give it a nice default height */
}

.button-group {
  flex-shrink: 0;
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #eee;
  background: #fff;
}

button {
  width: 48%;
  padding: 10px;
  font-size: 16px;
  cursor: pointer;
  background-color: #3498db;
  color: white;
  border: none;
  border-radius: 5px;
  transition: background-color 0.3s;
}

button:hover {
  background-color: #2980b9;
}

button[type="button"] {
  background-color: #e74c3c;
}

button[type="button"]:hover {
  background-color: #c0392b;
}

button:disabled {
  background-color: #bdc3c7;
  cursor: not-allowed;
}
</style>
